import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../contexts/AuthContext';

interface ResetPasswordPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, session, loading: authLoading } = useAuth();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [resetMode, setResetMode] = useState<'recovery' | 'dashboard' | 'unknown'>('unknown');
  const [modeChecked, setModeChecked] = useState(false);

  useEffect(() => {
    const determineMode = async () => {
      
      // Attendre que l'authentification soit initialisée
      if (authLoading) {
        return;
      }
      
      // Si on a déjà déterminé le mode, ne pas re-vérifier
      if (modeChecked) {
        return;
      }

      // Vérifier d'abord s'il y a des tokens de récupération dans l'URL
      
      const hash = window.location.hash.substring(1);
      const hashParams = new URLSearchParams(hash);
      
      // Vérifier s'il y a une erreur dans l'URL (lien expiré ou invalide)
      const error = hashParams.get('error');
      const errorCode = hashParams.get('error_code');

      
      if (error) {
        
        if (errorCode === 'otp_expired') {
          showToast('Le lien de réinitialisation a expiré. Demandez-en un nouveau.', 'error');
        } else {
          showToast('Lien invalide ou expiré. Demandez un nouveau lien.', 'error');
        }
        
        setTimeout(() => {
          navigate('/forgot-password');
        }, 2000);
        return;
      }
      
      const type = hashParams.get('type') || searchParams.get('type');
      const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');

      // MODE 1: Lien magique de récupération (PRIORITÉ ABSOLUE)
      if (type === 'recovery' && accessToken && refreshToken) {
        try {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (error) {
            showToast('Lien de récupération invalide ou expiré', 'error');
            navigate('/login');
            return;
          }
          
          // Attendre un peu que le contexte Auth se mette à jour
          await new Promise(resolve => setTimeout(resolve, 500));
          
          setResetMode('recovery');
          setIsValidToken(true);
          setIsLoading(false);
          setModeChecked(true); // Marquer comme vérifié pour éviter re-trigger
          // Nettoyer l'URL en supprimant les tokens sensibles
          window.history.replaceState({}, document.title, '/reset-password');
          return; // IMPORTANT: stopper ici, ne pas aller dans les autres conditions
          
        } catch (err) {
          showToast('Erreur lors de la vérification du lien', 'error');
          navigate('/login');
          return;
        }
      }
      // MODE 2: Changement depuis le dashboard (utilisateur connecté)
      else if (user && session) {
        setResetMode('dashboard');
        setIsValidToken(true);
        setModeChecked(true);
      }
      // MODE 3: Accès non autorisé (MAIS PAS si on a des tokens recovery en cours de traitement)
      else if (type !== 'recovery') {
        if (!type) {
          // Si pas de type, c'est probablement un accès direct non autorisé
          showToast('Vous devez être connecté pour changer votre mot de passe', 'error');
        } else {
          showToast('Lien de récupération invalide ou expiré', 'error');
        }
        navigate('/login');
        return;
      } else {
      }
      
      setIsLoading(false);
    };

    determineMode();
  }, [searchParams, navigate, showToast, user, session, authLoading, modeChecked]);

  const validateForm = () => {
    if (!newPassword || !confirmPassword) {
      showToast('Veuillez remplir tous les champs', 'error');
      return false;
    }

    if (newPassword.length < 8) {
      showToast('Le nouveau mot de passe doit contenir au moins 8 caractères', 'error');
      return false;
    }

    // Validation mot de passe fort (requis par Supabase)
    if (!/[a-z]/.test(newPassword)) {
      showToast('Le mot de passe doit contenir au moins une minuscule (a-z)', 'error');
      return false;
    }

    if (!/[A-Z]/.test(newPassword)) {
      showToast('Le mot de passe doit contenir au moins une majuscule (A-Z)', 'error');
      return false;
    }

    if (!/[0-9]/.test(newPassword)) {
      showToast('Le mot de passe doit contenir au moins un chiffre (0-9)', 'error');
      return false;
    }

    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(newPassword)) {
      showToast('Le mot de passe doit contenir au moins un caractère spécial (!@#$...)', 'error');
      return false;
    }

    if (newPassword !== confirmPassword) {
      showToast('Les nouveaux mots de passe ne correspondent pas', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // MODE DASHBOARD: L'utilisateur doit entrer son mot de passe actuel pour sécurité
      // MAIS on ne le vérifie PAS ici (évite pb captcha + l'utilisateur est déjà connecté)
      // Supabase.auth.updateUser() échouera automatiquement si la session n'est pas valide
      
      if (resetMode === 'dashboard') {
      }

      // Mettre à jour le mot de passe (fonctionne pour les deux modes)
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        if (error.message.includes('session')) {
          showToast('Session expirée, veuillez vous reconnecter', 'error');
          navigate('/login');
        } else {
          showToast('Erreur lors de la mise à jour du mot de passe', 'error');
        }
        return;
      }

      showToast('Mot de passe changé avec succès !', 'success');
      
      // Navigation différente selon le mode
      setTimeout(() => {
        if (resetMode === 'recovery') {
          // Après récupération, rediriger vers login
          showToast('Veuillez vous reconnecter avec votre nouveau mot de passe', 'success');
          navigate('/login');
        } else {
          // Depuis dashboard, retourner au dashboard ou profil
          navigate('/profile');
        }
      }, 2000);
      
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">
            {resetMode === 'unknown' ? 'Vérification des autorisations...' : 'Chargement...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès non autorisé</h2>
          <p className="text-gray-600 mb-4">
            {resetMode === 'unknown' 
              ? 'Vous devez être connecté ou avoir un lien valide pour accéder à cette page.'
              : 'Ce lien de récupération est invalide ou a expiré.'
            }
          </p>
          <button 
            onClick={() => navigate('/login')} 
            className="bg-[#002c46] text-white px-6 py-2 rounded-lg hover:bg-[#001a2a] transition-colors"
          >
            {resetMode === 'unknown' ? 'Se connecter' : 'Retour à la connexion'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-[#002c46] rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-key text-white text-2xl"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {resetMode === 'recovery' ? 'Nouveau mot de passe' : 'Changer le mot de passe'}
          </h2>
          <p className="text-gray-600">
            {resetMode === 'recovery' 
              ? 'Choisissez un nouveau mot de passe sécurisé'
              : 'Vous êtes connecté, choisissez un nouveau mot de passe sécurisé'
            }
          </p>
          {resetMode === 'dashboard' && user?.email && (
            <p className="text-sm text-blue-600 mt-2">
              <i className="fas fa-user text-xs mr-1"></i>
              Connecté en tant que: {user.email}
            </p>
          )}
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {resetMode === 'recovery' ? 'Nouveau mot de passe' : 'Nouveau mot de passe'} *
              </label>
              <input
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                placeholder="••••••••"
              />
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs font-semibold text-gray-700 mb-1">
                  <i className="fas fa-shield-alt text-[#002c46] mr-1"></i>
                  Votre mot de passe doit contenir :
                </p>
                <ul className="text-xs text-gray-600 space-y-1 ml-5">
                  <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>
                    <i className={`fas ${newPassword.length >= 8 ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                    Au moins 8 caractères
                  </li>
                  <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>
                    <i className={`fas ${/[a-z]/.test(newPassword) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                    Une minuscule (a-z)
                  </li>
                  <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>
                    <i className={`fas ${/[A-Z]/.test(newPassword) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                    Une majuscule (A-Z)
                  </li>
                  <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''}>
                    <i className={`fas ${/[0-9]/.test(newPassword) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                    Un chiffre (0-9)
                  </li>
                  <li className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(newPassword) ? 'text-green-600' : ''}>
                    <i className={`fas ${/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(newPassword) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                    Un caractère spécial (!@#$...)
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Confirmer le nouveau mot de passe *
              </label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-6 bg-[#002c46] text-white rounded-lg hover:bg-[#001a2a] transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Mise à jour en cours...
                </>
              ) : (
                <>
                  <i className="fas fa-check mr-2"></i>
                  {resetMode === 'recovery' ? 'Définir le nouveau mot de passe' : 'Changer le mot de passe'}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center space-y-2">
            {resetMode === 'recovery' ? (
              <button
                onClick={() => navigate('/login')}
                className="text-gray-500 hover:text-gray-700 font-medium text-sm"
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Retour à la connexion
              </button>
            ) : (
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate('/profile')}
                  className="text-gray-500 hover:text-gray-700 font-medium text-sm"
                >
                  <i className="fas fa-user mr-2"></i>
                  Mon profil
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-gray-500 hover:text-gray-700 font-medium text-sm"
                >
                  <i className="fas fa-tachometer-alt mr-2"></i>
                  Dashboard
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
